import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  input,
  OnDestroy,
  output,
  type InputSignal,
  type OutputEmitterRef,
  type Signal,
  viewChild,
} from '@angular/core';
import { UiBottomSheetSnap } from '../../types/ui-bottom-sheet-snap.type';
import { UiBottomSheetSize } from '../../types/ui-bottom-sheet-size.type';

const FOCUSABLE_SELECTOR =
  'a[href], button, input, select, textarea, [tabindex], [contenteditable="true"]';

interface IndexedFocusableElement {
  readonly element: HTMLElement;
  readonly documentOrder: number;
  readonly tabIndex: number;
}

@Component({
  selector: 'ui-bottom-sheet',
  imports: [],
  templateUrl: './ui-bottom-sheet.component.html',
  styleUrl: './ui-bottom-sheet.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UiBottomSheetComponent implements OnDestroy {
  private static nextId = 0;

  private previouslyFocusedElement: HTMLElement | null = null;

  public readonly open: InputSignal<boolean> = input<boolean>(false);
  public readonly title: InputSignal<string | null> = input<string | null>(null);
  public readonly description: InputSignal<string | null> = input<string | null>(null);
  public readonly ariaLabel: InputSignal<string | null> = input<string | null>(null);
  public readonly closeLabel: InputSignal<string> = input<string>('Close');
  public readonly closeIcon: InputSignal<boolean> = input<boolean>(false);
  public readonly closeOnBackdrop: InputSignal<boolean> = input<boolean>(true);
  public readonly showHandle: InputSignal<boolean> = input<boolean>(true);
  public readonly showHeader: InputSignal<boolean> = input<boolean>(true);
  public readonly size: InputSignal<UiBottomSheetSize> = input<UiBottomSheetSize>('md');
  public readonly snap: InputSignal<UiBottomSheetSnap> = input<UiBottomSheetSnap>('content');
  public readonly closed: OutputEmitterRef<void> = output<void>();

  protected readonly fallbackId: string = `ui-bottom-sheet-${(UiBottomSheetComponent.nextId += 1)}`;
  protected readonly titleId: Signal<string> = computed((): string => `${this.fallbackId}-title`);
  protected readonly descriptionId: Signal<string> = computed((): string => `${this.fallbackId}-description`);
  protected readonly hasTitle: Signal<boolean> = computed((): boolean => !!this.title());
  protected readonly hasDescription: Signal<boolean> = computed((): boolean => !!this.description());
  protected readonly accessibleLabel: Signal<string | null> = computed((): string | null => this.ariaLabel());
  protected readonly labelledBy: Signal<string | null> = computed((): string | null => {
    if (this.ariaLabel()) {
      return null;
    }

    return this.hasTitle() ? this.titleId() : null;
  });
  protected readonly describedBy: Signal<string | null> = computed(
    (): string | null => (this.hasDescription() ? this.descriptionId() : null),
  );
  protected readonly isSmall: Signal<boolean> = computed((): boolean => this.size() === 'sm');
  protected readonly isMedium: Signal<boolean> = computed((): boolean => this.size() === 'md');
  protected readonly isLarge: Signal<boolean> = computed((): boolean => this.size() === 'lg');
  protected readonly isContentSnap: Signal<boolean> = computed((): boolean => this.snap() === 'content');
  protected readonly isLargeSnap: Signal<boolean> = computed((): boolean => this.snap() === 'large');
  protected readonly panel: Signal<ElementRef<HTMLElement> | undefined> = viewChild<ElementRef<HTMLElement>>('panel');

  private readonly focusOpenDialog = effect((): void => {
    if (this.open()) {
      this.previouslyFocusedElement = getActiveHTMLElement();
      queueMicrotask((): void => this.focusInitialElement());

      return;
    }

    this.restoreFocus();
  });

  public ngOnDestroy(): void {
    this.restoreFocus();
  }

  protected handleCloseRequested(): void {
    this.closed.emit();
  }

  protected handleBackdropSelected(): void {
    if (this.closeOnBackdrop()) {
      this.closed.emit();
    }
  }

  protected handleDialogKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.closed.emit();

      return;
    }

    if (event.key === 'Tab') {
      this.trapFocus(event);
    }
  }

  private focusInitialElement(): void {
    const panelElement: HTMLElement | null = this.getPanelElement();

    if (!panelElement) {
      return;
    }

    const focusableElements: readonly HTMLElement[] = this.getFocusableElements(panelElement);
    const targetElement: HTMLElement = focusableElements[0] ?? panelElement;

    targetElement.focus();
  }

  private restoreFocus(): void {
    const elementToRestore: HTMLElement | null = this.previouslyFocusedElement;

    this.previouslyFocusedElement = null;

    if (this.focusOpenPeerBottomSheet()) {
      return;
    }

    if (!elementToRestore || typeof document === 'undefined' || !document.contains(elementToRestore)) {
      return;
    }

    elementToRestore.focus();
  }

  private trapFocus(event: KeyboardEvent): void {
    const panelElement: HTMLElement | null = this.getPanelElement();

    if (!panelElement) {
      return;
    }

    const focusableElements: readonly HTMLElement[] = this.getFocusableElements(panelElement);

    if (focusableElements.length === 0) {
      event.preventDefault();
      panelElement.focus();

      return;
    }

    const firstElement: HTMLElement = focusableElements[0] as HTMLElement;
    const lastElement: HTMLElement = focusableElements[focusableElements.length - 1] as HTMLElement;
    const activeElement: HTMLElement | null = getActiveHTMLElement();

    if (event.shiftKey && (activeElement === firstElement || activeElement === panelElement)) {
      event.preventDefault();
      lastElement.focus();

      return;
    }

    if (!event.shiftKey && activeElement === lastElement) {
      event.preventDefault();
      firstElement.focus();
    }
  }

  private getPanelElement(): HTMLElement | null {
    return this.panel()?.nativeElement ?? null;
  }

  private getFocusableElements(panelElement: HTMLElement): readonly HTMLElement[] {
    return Array.from(panelElement.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR))
      .map(
        (element: HTMLElement, documentOrder: number): IndexedFocusableElement => ({
          element,
          documentOrder,
          tabIndex: getTabIndex(element),
        }),
      )
      .filter((entry: IndexedFocusableElement): boolean => isFocusableElement(entry))
      .sort(compareFocusableElements)
      .map((entry: IndexedFocusableElement): HTMLElement => entry.element);
  }

  private focusOpenPeerBottomSheet(): boolean {
    if (typeof document === 'undefined') {
      return false;
    }

    const currentPanelElement: HTMLElement | null = this.getPanelElement();
    const peerPanels: readonly HTMLElement[] = Array.from(
      document.querySelectorAll<HTMLElement>('.ui-bottom-sheet-panel[role="dialog"][aria-modal="true"]'),
    ).filter((panelElement: HTMLElement): boolean => panelElement !== currentPanelElement);

    const activeElement: HTMLElement | null = getActiveHTMLElement();

    if (activeElement && peerPanels.some((panelElement: HTMLElement): boolean => panelElement.contains(activeElement))) {
      return true;
    }

    const targetPanel: HTMLElement | undefined = peerPanels[peerPanels.length - 1];

    if (!targetPanel) {
      return false;
    }

    const focusableElements: readonly HTMLElement[] = this.getFocusableElements(targetPanel);
    const targetElement: HTMLElement = focusableElements[0] ?? targetPanel;

    targetElement.focus();

    return true;
  }
}

function getActiveHTMLElement(): HTMLElement | null {
  if (typeof document === 'undefined') {
    return null;
  }

  return document.activeElement instanceof HTMLElement ? document.activeElement : null;
}

function isFocusableElement(entry: IndexedFocusableElement): boolean {
  return (
    entry.tabIndex >= 0 &&
    !entry.element.hidden &&
    entry.element.getAttribute('aria-hidden') !== 'true' &&
    !isDisabledElement(entry.element)
  );
}

function isDisabledElement(element: HTMLElement): boolean {
  return (
    'disabled' in element &&
    Boolean((element as HTMLButtonElement | HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement).disabled)
  );
}

function getTabIndex(element: HTMLElement): number {
  const explicitTabIndex: string | null = element.getAttribute('tabindex');

  if (explicitTabIndex === null) {
    return 0;
  }

  const parsedTabIndex = Number(explicitTabIndex);

  return Number.isFinite(parsedTabIndex) ? Math.trunc(parsedTabIndex) : -1;
}

function compareFocusableElements(
  first: IndexedFocusableElement,
  second: IndexedFocusableElement,
): number {
  const firstTabOrder: number = getTabOrder(first.tabIndex);
  const secondTabOrder: number = getTabOrder(second.tabIndex);

  if (firstTabOrder !== secondTabOrder) {
    return firstTabOrder - secondTabOrder;
  }

  return first.documentOrder - second.documentOrder;
}

function getTabOrder(tabIndex: number): number {
  return tabIndex > 0 ? tabIndex : Number.MAX_SAFE_INTEGER;
}
